import React, { HTMLProps, ReactNode, useEffect } from 'react'
import { useCurrentSize } from '@shared/lib/helpers/classes'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import s from './MainCard.module.sass'
import Link from '@shared/ui/Navigation/Link'
import IconChevronLong from '@icons/icon-selection-chevron--big.svg'
import { ChevronWithTooltip } from '@shared/ui/Feedback/ChevronWithTooltip'
import { MediaImg } from '../../../Media/MediaImg'
import { GetResizedLinkWithCustomSize } from '@shared/ui/Media/MediaImg/helpers/helper'

const IS_DEV = process.env.NODE_ENV === 'development'

interface Props {
  img: string
  alt: string
  href: string
  sizes?: string // XL - desktop, S - mobile
  chevron?: boolean
  classNameWrapper?: string
  classNameContent?: string
  layout?: 'default' | 'feature'
  children: ReactNode
}

type MainCardProps = Props & HTMLProps<HTMLAnchorElement>

type MainCardLinkRest = Omit<
  MainCardProps,
  'img' | 'href' | 'alt' | 'sizes' | 'chevron' | 'classNameWrapper' | 'classNameContent' | 'layout' | 'children'
>

const useMainCardMissingCoverDevLog = (input: {
  img: string | null | undefined
  href: string
  alt: string
  chevron: boolean | undefined
  sizes: string
  classNameWrapper: string
  classNameContent: string
  children: ReactNode
  linkRest: MainCardLinkRest
}) => {
  const { img, href, alt, chevron, sizes, classNameWrapper, classNameContent, children, linkRest } = input
  const imgMissing = img == null || (typeof img === 'string' && img.trim().length === 0)

  useEffect(() => {
    if (!IS_DEV || !imgMissing) return

    const childrenHint = (() => {
      if (typeof children === 'string') return children.slice(0, 160)
      if (typeof children === 'number') return String(children)
      if (React.isValidElement(children)) {
        const t = children.type
        if (typeof t === 'string') return `<${t}>`
        const named = t as { displayName?: string; name?: string }
        return named.displayName || named.name || 'ReactElement'
      }
      if (Array.isArray(children)) return `fragment/array (${children.length} items)`
      if (children == null) return String(children)
      return typeof children
    })()

    console.warn('[MainCard] missing cover image', {
      href,
      alt,
      img,
      imgType: typeof img,
      chevron,
      sizes,
      classNameWrapper,
      classNameContent,
      childrenHint,
      linkRestKeys: Object.keys(linkRest),
      linkClassName: typeof linkRest.className === 'string' ? linkRest.className : undefined,
    })
  }, [imgMissing, href, alt, img, chevron, sizes, classNameWrapper, classNameContent])
}

const MainCard = React.forwardRef<HTMLDivElement, MainCardProps>((params, ref) => {
  const {
    img,
    href,
    alt,
    sizes = 'XL S',
    chevron,
    classNameWrapper = '',
    classNameContent = '',
    layout = 'default',
    children,
    ...linkRest
  } = params

  useMainCardMissingCoverDevLog({
    img,
    href,
    alt,
    chevron,
    sizes,
    classNameWrapper,
    classNameContent,
    children,
    linkRest,
  })

  const [size] = useCurrentSize(sizes)
  const isFeature = layout === 'feature'
  const borderRadius = { XL: '32px', S: isFeature ? '20px' : '24px' } as const
  const imgSize = isFeature ? { width: 395, height: 515 } : { width: 500, height: 500 }

  const image = {
    url: img,
    width: imgSize.width,
    height: imgSize.height,
    description: alt,
  }

  const backdropBlurUrl = GetResizedLinkWithCustomSize(image, { width: 8, height: 8 })

  return (
    <BlockBoundary>
      <Link
        href={href}
        className={`${s.card} ${classNameWrapper}`}
        data-chevron={chevron}
        data-cover-size={size}
        data-layout={layout}
        scroll={false}
        title={alt}
        {...linkRest}>
        {chevron && <ChevronWithTooltip icon={IconChevronLong} className={s.chevron} />}

        <MediaImg
          image={image}
          objectId={Number(img)}
          logContext={`main_card:${href}`}
          responsiveSizes={
            isFeature
              ? {
                  'desktop-large': '395x515',
                  desktop: '395x515',
                  tablet: '395x515',
                  mobile: '306x306',
                }
              : {
                  'desktop-large': '532x532',
                  desktop: '456x456',
                  tablet: '344x344',
                  mobile: '310x310',
                }
          }
          flexible={true}
          className={s['img-wrapper']}
          dimmed={'gradient'}
          borderRadius={borderRadius[size as keyof typeof borderRadius]}
        />

        <div className={`${s.content} ${classNameContent}`} ref={ref}>
          {children}
        </div>

        <div className={s['backdrop-wrap']} data-cover-size={size}>
          <div
            className={s.backdrop}
            data-cover-size={size}
            style={backdropBlurUrl ? { backgroundImage: `url(${backdropBlurUrl})` } : undefined}
          />
        </div>
      </Link>
    </BlockBoundary>
  )
})

MainCard.displayName = 'MainCard'

export default MainCard
